import { useTranslation } from '@/feature/lang';
import type { AllTexts, Translation } from '@/feature/lang/types';
import type { ColumnDef, RowData } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import Translate from '../Translate';
import { getColumnDefaults } from './column';
import type { ExtendedColumnDef } from './types';

function getColumnName(
  columnName: ((texts: AllTexts) => Translation) | undefined,
  headerText: ((texts: AllTexts) => Translation) | undefined,
  texts: AllTexts
): Translation {
  if (columnName) {
    return columnName(texts);
  }
  if (headerText) {
    return headerText(texts);
  }
  return '';
}

export const useExtendedColumnDefs = <TData extends RowData>(
  columnDefs: ExtendedColumnDef<TData>[]
): ColumnDef<TData>[] => {
  const { texts, translate } = useTranslation();

  const createColumnDef = useCallback(
    <TData extends RowData>({
      headerText = () => '',
      columnName,
      columns = [],
      ...other
    }: ExtendedColumnDef<TData>): ColumnDef<TData> => {
      const columnDefaults = getColumnDefaults(other.meta?.dataType);
      const columnNameTranslation = getColumnName(
        columnName,
        headerText,
        texts
      );

      return {
        ...columnDefaults,
        ...other,
        header: other.header ?? (() => <Translate text={headerText(texts)} />),
        meta: {
          ...columnDefaults?.meta,
          ...other.meta,
          name: translate(columnNameTranslation),
        },
        columns: columns.map(createColumnDef),
      } as ColumnDef<TData>;
    },
    [texts, translate]
  );

  return useMemo(
    () => columnDefs.map(createColumnDef),
    [columnDefs, createColumnDef]
  );
};
